import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { certificationStatus } from '../../lib/training';
import { notifyCertRequested, notifyCertReminder } from '../../lib/notifications';
import {
  bgCheckTypeFor, PROVISIONAL_DAYS, provisionalExpiry, daysUntil,
  CLINICAL_CERT_TYPES, needsClinicalCerts,
  startGchexsCheck, clearBackgroundCheck, flagBackgroundCheck, markLicenseVerified, logOnboardingEvent,
} from '../../lib/staffCompliance';

// Moved out of OnboardingPipeline.js (2026-08-08), verbatim, so
// PersonnelProfile.js (and any future page) can show the same
// compliance/certification UI for a staff member regardless of whether
// they're still in the onboarding kanban — OnboardingPipeline.js's own
// queries exclude active staff entirely, so these components previously had
// no way to reach a fast-tracked or already-active staff member.

const btnStyle = (bg) => ({ padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: bg, color: '#fff' });
const inputStyle = { width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' };

export const StatusPill = ({ status }) => (
  <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: status.bg, color: status.color }}>{status.label}</span>
);

export function BgCheckBadge({ staff: s }) {
  const type = s.bg_check_type || bgCheckTypeFor(s);
  if (type === 'license_verification') {
    return s.bg_check_status === 'cleared'
      ? <div style={{ fontSize: 9, color: 'var(--success)', marginTop: 4 }}>● License verified</div>
      : <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 4 }}>○ License not yet verified</div>;
  }
  if (s.bg_check_status === 'cleared') return <div style={{ fontSize: 9, color: 'var(--success)', marginTop: 4 }}>● GCHEXS cleared</div>;
  if (s.bg_check_status === 'flagged') return <div style={{ fontSize: 9, color: 'var(--danger)', marginTop: 4, fontWeight: 700 }}>⚠ GCHEXS flagged</div>;
  if (s.bg_check_status === 'pending') {
    const left = daysUntil(provisionalExpiry(s.bg_check_initiated_at));
    return <div style={{ fontSize: 9, color: left != null && left <= 5 ? 'var(--warn)' : 'var(--text3)', marginTop: 4 }}>
      ● GCHEXS pending{left != null ? ` (${left > 0 ? `${left}d left` : 'expired'})` : ''}
    </div>;
  }
  return <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 4 }}>○ GCHEXS not started</div>;
}

// Self-contained version for pages outside OnboardingPipeline.js (currently
// just PersonnelProfile.js) — manages its own activeStaffOptions/provisional
// supervisor/busy state and calls the shared mutations in staffCompliance.js
// directly, then reports back via onUpdated so the parent page can refresh
// its own copy of the staff record. OnboardingPipeline.js keeps its own
// parent-controlled BackgroundCheckPanel + handlers unchanged (same UI,
// same underlying rules) — duplicated intentionally here to avoid touching
// that page's already-working, tightly-coupled state wiring.
export function BackgroundCheckPanel({ staff: s, onUpdated }) {
  const { profile } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activeStaffOptions, setActiveStaffOptions] = useState([]);
  const [provisionalSupervisorId, setProvisionalSupervisorId] = useState('');
  const [consentSigned, setConsentSigned] = useState(null);

  useEffect(() => {
    supabase.from('staff').select('id, full_name').eq('status', 'active').order('full_name')
      .then(({ data }) => setActiveStaffOptions((data || []).filter(a => a.id !== s.id)));
  }, [s.id]);

  useEffect(() => {
    let active = true;
    supabase.from('staff_documents').select('status').eq('staff_id', s.id).eq('doc_type', 'background_check_consent').maybeSingle()
      .then(({ data }) => { if (active) setConsentSigned(data?.status === 'signed'); });
    return () => { active = false; };
  }, [s.id]);

  const logEvent = (staffId, eventType, detail, prev, next) => logOnboardingEvent(staffId, eventType, detail, prev, next, profile?.full_name);

  const run = async (fn) => {
    setBusy(true); setError('');
    try {
      const patch = await fn();
      onUpdated?.(patch);
    } catch (err) {
      console.error('Background check action failed:', err);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const onStart = () => run(() => startGchexsCheck(s.id, provisionalSupervisorId, logEvent));
  const onClear = () => run(() => clearBackgroundCheck(s, logEvent));
  const onFlag = () => {
    if (!window.confirm('Mark this background check as flagged/unsatisfactory? Per Policy A-2.1 this person should not be assigned client care.')) return;
    run(() => flagBackgroundCheck(s.id, logEvent));
  };
  // Records who verified — license_verified_by is the evidence the GCHEXS
  // exemption rests on, and this panel was the one path not passing it.
  const onVerifyLicense = () => run(() => markLicenseVerified(s.id, logEvent, profile?.full_name));

  const type = s.bg_check_type || bgCheckTypeFor(s);
  const box = (bg, border) => ({ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '10px 12px' });

  const errorBanner = error && <div style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 8 }}>{error}</div>;

  if (type === 'license_verification') {
    if (s.bg_check_status === 'cleared') {
      return <div style={box('var(--success-bg)', 'var(--success)')}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)' }}>✓ License Verified</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>RN/LPN — exempt from GCHEXS under Georgia SB 406 (Policy A-2.1).</div>
      </div>;
    }
    return <div style={box('var(--bg2)', 'var(--border)')}>
      {errorBanner}
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>License Verification</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>RN/LPN are exempt from GCHEXS — verify the license instead.</div>
      <a href="https://www.nursys.com/LQC/LQCTerms.aspx" target="_blank" rel="noreferrer" style={{ ...btnStyle('var(--accent)'), display: 'block', textAlign: 'center', textDecoration: 'none', marginBottom: 8 }}>Verify on Nursys ↗</a>
      <button style={{ ...btnStyle('var(--success)'), width: '100%' }} onClick={onVerifyLicense} disabled={busy}>Mark Verified</button>
    </div>;
  }

  if (s.bg_check_status === 'cleared') {
    return <div style={box('var(--success-bg)', 'var(--success)')}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--success)' }}>✓ GCHEXS Cleared</div>
    </div>;
  }
  if (s.bg_check_status === 'flagged') {
    return <div style={box('var(--danger-bg)', 'var(--danger)')}>
      {errorBanner}
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--danger)' }}>⚠ FLAGGED — Unsatisfactory Determination</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', margin: '4px 0 8px' }}>Per Policy A-2.1: do not assign client care. Review before proceeding.</div>
      <button style={{ ...btnStyle('var(--success)'), width: '100%' }} onClick={onClear} disabled={busy}>Mark Cleared (correction)</button>
    </div>;
  }
  if (s.bg_check_status === 'pending') {
    const left = daysUntil(provisionalExpiry(s.bg_check_initiated_at));
    const supervisor = activeStaffOptions.find(a => a.id === s.provisional_supervisor_id);
    return <div style={box(left != null && left <= 5 ? 'var(--danger-bg)' : 'var(--warn-bg)', left != null && left <= 5 ? 'var(--danger)' : 'var(--warn)')}>
      {errorBanner}
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--warn)' }}>GCHEXS Pending — Provisional Start</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', margin: '4px 0 8px' }}>
        {left != null ? (left > 0 ? `${left} day${left === 1 ? '' : 's'} left in the ${PROVISIONAL_DAYS}-day provisional window.` : 'Provisional window has expired.') : ''}
        {' '}{supervisor ? `Supervised by ${supervisor.full_name}.` : 'No provisional supervisor documented yet.'}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={{ ...btnStyle('var(--success)'), flex: 1 }} onClick={onClear} disabled={busy}>Mark Cleared</button>
        <button style={{ ...btnStyle('var(--danger)'), flex: 1 }} onClick={onFlag} disabled={busy}>Mark Flagged</button>
      </div>
    </div>;
  }
  // not_started
  return <div style={box('var(--bg2)', 'var(--border)')}>
    {errorBanner}
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>GCHEXS Background Check</div>
    <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
      Office initiates the GCHEXS application (SOP-HR01); can run in parallel with everything else.
      A {PROVISIONAL_DAYS}-day provisional start under direct supervision is allowed while pending (Policy A-2.1).
    </div>
    {consentSigned === false && (
      <div style={{ fontSize: 11, color: 'var(--warn)', marginBottom: 8 }}>No signed Background Check Consent on file — send one from the Personnel File tab below before submitting the GCHEXS application.</div>
    )}
    <select value={provisionalSupervisorId} onChange={e => setProvisionalSupervisorId(e.target.value)} style={{ ...inputStyle, marginBottom: 8 }}>
      <option value="">Provisional supervisor (optional, can add later)</option>
      {activeStaffOptions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
    </select>
    <button style={{ ...btnStyle('var(--accent)'), width: '100%' }} onClick={onStart} disabled={busy}>Mark Application Submitted</button>
  </div>;
}

// Always visible regardless of onboarding_status — clinical certs run in
// parallel with everything else, never a stop in the process.
export function ClinicalCertsPanel({ staff }) {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingType, setAddingType] = useState(null);
  const [issuedDate, setIssuedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('staff_certifications').select('*')
      .eq('staff_id', staff.id).in('cert_type', CLINICAL_CERT_TYPES.map(c => c.key))
      .order('expiry_date', { ascending: false });
    setCerts(data || []);
    setLoading(false);
  }, [staff.id]);

  useEffect(() => { load(); }, [load]);

  if (!needsClinicalCerts(staff)) return <div className="empty-state"><p>Not required for this role.</p></div>;
  if (loading) return <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Loading clinical requirements…</div>;

  const isEs = staff.preferred_language === 'es';
  const label = (type) => (isEs ? type.labelEs : type.label);

  const request = async (type) => {
    setBusy(true); setError('');
    try {
      const existing = certs.find(c => c.cert_type === type.key);
      if (!existing) {
        const { error } = await supabase.from('staff_certifications').insert([{
          staff_id: staff.id, cert_type: type.key, cert_label: type.label, status: 'requested',
          notes: 'Requested',
        }]);
        if (error) throw error;
      }
      await notifyCertRequested(staff.email, staff.full_name, label(type), isEs ? 'es' : 'en');
      load();
    } catch (err) {
      console.error('Cert request failed:', err);
      setError('Request failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  const remind = async (type, record) => {
    setBusy(true); setError('');
    try {
      await notifyCertReminder(staff.email, staff.full_name, label(type), isEs ? 'es' : 'en');
      const { error: updateErr } = await supabase.from('staff_certifications').update({ reminder_sent_at: new Date().toISOString() }).eq('id', record.id);
      if (updateErr) throw updateErr;
      load();
    } catch (err) {
      console.error('Cert reminder failed:', err);
      setError('Reminder failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  const startAdd = (type) => {
    setAddingType(type.key);
    setIssuedDate('');
    const exp = new Date(); exp.setMonth(exp.getMonth() + type.months);
    setExpiryDate(exp.toISOString().slice(0, 10));
  };

  const save = async (type) => {
    if (!expiryDate) return;
    setBusy(true); setError('');
    try {
      const existing = certs.find(c => c.cert_type === type.key);
      const isAgency = staff.employment_type === 'agency';
      const payload = {
        staff_id: staff.id, cert_type: type.key, cert_label: type.label, status: 'active',
        issued_date: issuedDate || null, expiry_date: expiryDate,
        verifying_party: isAgency ? 'sending_agency' : 'ahfc_direct',
        notes: isAgency ? 'Filed from sending agency documentation (Policy A-2.4)' : 'Added',
      };
      const { error: saveErr } = existing
        ? await supabase.from('staff_certifications').update(payload).eq('id', existing.id)
        : await supabase.from('staff_certifications').insert([payload]);
      if (saveErr) throw saveErr;
      setAddingType(null);
      load();
    } catch (err) {
      console.error('Cert save failed:', err);
      setError('Save failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {error && <div style={{ fontSize: 11, color: 'var(--danger)', background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>{error}</div>}
      {staff.employment_type === 'agency' && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
          Agency worker — "Mark On File" here files the sending agency's current documentation (Policy A-2.4); no need to re-run these independently.
        </div>
      )}
      {CLINICAL_CERT_TYPES.map(type => {
        const record = certs.find(c => c.cert_type === type.key);
        const status = record ? certificationStatus(record) : null;
        const requestedDaysAgo = (status?.key === 'requested' && record?.created_at)
          ? Math.floor((Date.now() - new Date(record.created_at).getTime()) / 86400000) : null;
        return (
          <div key={type.key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text)' }}>{label(type)}</span>
              {status ? <StatusPill status={status} /> : <span style={{ fontSize: 11, color: 'var(--text3)' }}>Not on file</span>}
            </div>
            {status?.key === 'requested' && (
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
                Requested {requestedDaysAgo === 0 ? 'today' : `${requestedDaysAgo}d ago`} — awaiting response
                {record?.reminder_sent_at && ` · reminded ${format(new Date(record.reminder_sent_at), 'MMM d')}`}
              </div>
            )}
            {status && status.key !== 'requested' && record.expiry_date && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Expires {record.expiry_date}</div>}
            {addingType === type.key ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Issued</div>
                  <input type="date" value={issuedDate} onChange={e => setIssuedDate(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text3)' }}>Expires</div>
                  <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} style={inputStyle} />
                </div>
                <button style={btnStyle('var(--success)')} onClick={() => save(type)} disabled={busy}>Save</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {!record && (
                  <button style={{ ...btnStyle('var(--warn)'), fontSize: 11, padding: '5px 10px' }} onClick={() => request(type)} disabled={busy}>Request</button>
                )}
                {status?.key === 'requested' && (
                  <button style={{ ...btnStyle('var(--warn)'), fontSize: 11, padding: '5px 10px' }} onClick={() => remind(type, record)} disabled={busy}>Remind</button>
                )}
                <button style={{ ...btnStyle('var(--accent)'), fontSize: 11, padding: '5px 10px' }} onClick={() => startAdd(type)} disabled={busy}>
                  {status && status.key !== 'requested' ? 'Update' : 'Mark On File'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Compact version for the Activate warning — same completeness check as
// ClinicalCertsPanel above, without the add-record UI.
export function ClinicalCertsWarning({ staff }) {
  const [certs, setCerts] = useState(null);

  useEffect(() => {
    let active = true;
    if (!needsClinicalCerts(staff)) { setCerts(null); return; }
    supabase.from('staff_certifications').select('cert_type, expiry_date')
      .eq('staff_id', staff.id).in('cert_type', CLINICAL_CERT_TYPES.map(c => c.key))
      .then(({ data }) => { if (active) setCerts(data || []); });
    return () => { active = false; };
  }, [staff]);

  if (!needsClinicalCerts(staff) || certs === null) return null;
  const todayStr = new Date().toISOString().slice(0, 10);
  const missing = CLINICAL_CERT_TYPES.filter(type => {
    const record = certs.find(c => c.cert_type === type.key);
    return !record || !record.expiry_date || record.expiry_date < todayStr;
  });
  if (missing.length === 0) return null;

  return (
    <div style={{ fontSize: 11, color: 'var(--warn)', background: 'var(--warn-bg)', border: '1px solid var(--warn)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
      ⚠ {missing.map(m => m.label).join(' and ')} not on file or expired.
    </div>
  );
}
